/* @flow */
import AuthActions from 'auth/AuthActions';
import Button from 'components/Button.jsx';
import cx from 'classnames';
import Device from 'entities/Device';
import ActivityRecipient from 'entities/ActivityRecipient';
import Form from 'components/Form.jsx';
import FormInput from 'components/FormInput.jsx';
import humanparser from 'humanparser';
import invariant from 'invariant';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import React from 'react';
import Text from 'components/Text.jsx';
import ToastActions from 'actions/ToastActions';
import User from 'entities/User';
import Link from 'components/Link.jsx';

import './styles/PaymentViewConfirmationPhoneAuthentificator.scss';

type Props = {
  recipient: ActivityRecipient,
  onAuthSuccess: Function,
  className?: ?string,
};

type State = {
  loadingUserProfile: boolean,
  startCodeSignin: boolean,
  loading: boolean,
  userProfile: ?Object,
  token?: string,
  codeFormValid: boolean,
};

class PaymentViewConfirmationPhoneAuthentificator extends React.Component {
  props: Props;

  state: State = {
    loadingUserProfile: true,
    startCodeSignin: false,
    codeFormValid: false,
    userProfile: null,
    loading: false,
  };

  /* Load the user profile
   * This user profile is what help us determine
   * is the user already has a password or if the user
   * need to create a brand new account using sms verification code.
   */
  componentDidMount = () => {
    User
      .loadProfile({ user_identifier: this.props.recipient.getAddress() })
      .then((userProfile) => this.setState({ userProfile, loadingUserProfile: false }))
      .catch(() => this.setState({ loadingUserProfile: false }));
  }

  onCodeSigninStart = () => {
    this.setState({ startCodeSignin: true });
    this.refreshDeviceOutboundVerificationToken();
  }

  /* refreshed the verification token, this is the code send the the API
   * with the user code that he inputed in the form. This function is call
   * when the user start the verification process or when he clicks the
   * "send again" button in the form.
   */
  refreshDeviceOutboundVerificationToken = () => {
    // $FlowFixMe
    Device.outboundVerify(this.props.recipient.getAddress())
      .then(response => this.setState({ token: response.token }))
      .then(() => ToastActions.showSuccessToast('A verification code was send to the mobile phone'))
      .catch(err => console.error('PaymentViewConfirmationPhoneAuthentificator error verifying', err));
  }

  canSignInWithPassword = (profile: Object): boolean => {
    return profile && !profile.needs.password;
  }

  /* Handle the form submit for when the user profile
   * told us that the user already had a password.
   */
  handleSignIn = (data: Object) => {
    this.setState({ loading: true });

    const address = this.props.recipient.getAddress();

    invariant(address, 'recipient has no address, can‘t sign in');

    return AuthActions.signIn(address, data.password)
      .then(this.props.onAuthSuccess)
      .catch(ToastActions.showAPIError)
      .then(() => this.setState({ loading: false }));
  }

  /* Handle the form submit for when the user profile
   * told us that the user already did not have a password
   * and needed to undergo the device verification signup.
   */
  handleCodeSubmit = (values: Object) => {
    const updateUser = () => {
      const human = humanparser.parseName(this.props.recipient.getName());

      return User.update({
        first_name: human.firstName,
        last_name: human.lastName,
        password: values.password,
        role: '',
      });
    };

    this.setState({ loading: true });
    // $FlowFixMe
    Device.createOutboundDevice(this.state.token, values.code)
      .then(({ token }) =>
        AuthActions.accessTokenSignIn(token)
          .then(updateUser)
          .then(() => AuthActions.accessTokenSignIn(token))
      )
      .then(this.props.onAuthSuccess)
      .catch(ToastActions.showAPIError)
      .then(() => this.setState({ loading: false }))
      .catch(err => console.error('PaymentViewConfirmationPhoneAuthentificator createOutboundDevice error', err));
  }

  renderPasswordSignIn() {
    return (
      <Form onSubmit={this.handleSignIn}>
        <div className="PaymentViewConfirmationEmailAuthentificator-form-field">
          <Text weight="bold">Enter your Remind password to sign in</Text>
        </div>
        <FormInput
          type="password"
          name="password"
          autoComplete="current-password"
          autofocus
          placeholder="Enter your Remind password"
          required
          size="large"
          validations="minLength:1"
        />
        <Button
          type="submit"
          level="primary"
          loading={this.state.loading}
          className="PaymentViewConfirmationEmailAuthentificator-button"
          eventElementName="PaymentViewConfirmationEmailAuthentificator-button">
          Log in
        </Button>
      </Form>
    );
  }

  renderCodeSignIn() {
    if (!this.state.startCodeSignin) {
      return (
        <Button
          full
          type="submit"
          level="secondary"
          loading={this.state.loading}
          onClick={this.onCodeSigninStart}
          className="PaymentViewConfirmationPhoneAuthentificator-button"
          eventElementName="PaymentViewConfirmationPhoneAuthentificator-button">
          Signup on remind using your phone
        </Button>
      );
    }

    return (
      <Form
        onSubmit={this.handleCodeSubmit}
        onValid={() => this.setState({ codeFormValid: true })}
        onInvalid={() => this.setState({ codeFormValid: false })}>
        <div className="PaymentViewConfirmationPhoneAuthentificator-form-field">
          <Text weight="bold">Enter the code received on your phone.</Text>{' '}
          <Link onClick={this.refreshDeviceOutboundVerificationToken}>Resend Code</Link>
        </div>
        <FormInput
          required
          type="text"
          name="code"
          size="large"
          placeholder="Enter the 4 digit code"
          validations="minLength:1"
        />

        <FormInput
          required
          size="large"
          type="password"
          name="password"
          autoComplete="new-password"
          validations="minLength:6"
          validOnPristine={false}
          placeholder="New password for your account"
          validationError="Password must be at least 6 characters long."
        />

        <Button
          type="submit"
          level="primary"
          disabled={!this.state.codeFormValid}
          loading={this.state.loading}
          className="PaymentViewConfirmationPhoneAuthentificator-button"
          eventElementName="PaymentViewConfirmationPhoneAuthentificator-button">
          continue
        </Button>
      </Form>
    );
  }

  renderContent() {
    if (this.state.loadingUserProfile) {
      return <LoadingIndicator />;
    }

    if (!this.state.userProfile || this.canSignInWithPassword(this.state.userProfile)) {
      return this.renderPasswordSignIn();
    }

    return this.renderCodeSignIn();
  }

  render() {
    return (
      <div className={cx('PaymentViewConfirmationPhoneAuthentificator', this.props.className)}>
        {this.renderContent()}
      </div>
    );
  }
}

export default PaymentViewConfirmationPhoneAuthentificator;



// WEBPACK FOOTER //
// ./src/payments/components/PaymentViewConfirmationPhoneAuthentificator.jsx